/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants';

export const ZoomMethods = {
  _fitScale(width, height) {
    const fit = Math.min(width / this._imgWidth, height / this._imgHeight);
    return this.readonlyValue ? Math.min(fit, 1) : Math.max(MIN_SCALE, Math.min(MAX_SCALE, fit * 0.8));
  },

  _stageSize() {
    return { width: this._imgWidth, height: this._imgHeight };
  },

  _applyZoom(newScale, anchor = null) {
    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    const cx = anchor?.x ?? this._stage.width() / 2;
    const cy = anchor?.y ?? this._stage.height() / 2;
    const worldCx = (cx - this._stage.x()) / this._currentScale;
    const worldCy = (cy - this._stage.y()) / this._currentScale;

    this._currentScale = newScale;
    this._stage.scaleX(newScale);
    this._stage.scaleY(newScale);
    this._stage.x(cx - worldCx * newScale);
    this._stage.y(cy - worldCy * newScale);
    this._syncFogTextureTransform();
    this._stage.batchDraw();
    this._updateZoomDisplay();
    this._rememberEditorState?.();
  },

  _resetView() {
    if (!this._stage) return;

    const viewport = this.hasViewportTarget ? this.viewportTarget : this._stage.container();
    const vw = viewport.clientWidth || this._stage.width();
    const vh = viewport.clientHeight || this._stage.height();
    const scale = this._fitScale(vw, vh);

    this._stage.width(vw);
    this._stage.height(vh);
    this._currentScale = scale;
    this._stage.scaleX(scale);
    this._stage.scaleY(scale);
    this._stage.x((vw - this._imgWidth * scale) / 2);
    this._stage.y((vh - this._imgHeight * scale) / 2);
    this._syncFogTextureTransform();
    this._stage.batchDraw();
    this._updateZoomDisplay();
    this._rememberEditorState?.();
  },

  _updateZoomDisplay() {
    this._syncEffectRotationHandle();
    const scale = this.workspaceValue && this._positionManagerActive ? this._monitorZoom || 1 : this._currentScale;
    if (this.hasZoomDisplayTarget) this.zoomDisplayTarget.textContent = `${Math.round(scale * 100)}%`;
  }
};
